/**
 * Splits text by the configured delimiter(s).
 * @param text - The text to split
 * @param delimiter - The delimiter(s) to split by
 * @returns An array of trimmed, non-empty strings
 */
export const splitByDelimiter = (text: string, delimiter?: RegExp | string | string[]): string[] => {
  if (!delimiter) {
    return [text];
  }

  let parts: string[];

  if (delimiter instanceof RegExp) {
    parts = text.split(delimiter);
  } else if (Array.isArray(delimiter)) {
    // Create a regex from the array of delimiters, escaping special characters
    const escapedDelimiters = delimiter.map((d) => d.replaceAll(/[$()*+.?[\\\]^{|}]/g, String.raw`\$&`));
    const regex = new RegExp(escapedDelimiters.join('|'), 'g');
    parts = text.split(regex);
  } else {
    parts = text.split(delimiter);
  }

  // Trim and filter out empty strings
  return parts.map((part) => part.trim()).filter((part) => part.length > 0);
};

export interface TokenizeTextParams<T> {
  currentValues: T[];
  delimiter: RegExp | string | string[];
  freeSolo: boolean;
  getOptionDisabled?: (option: T) => boolean;
  getOptionLabel: (option: T) => string;
  options: T[];
  text: string;
}

/**
 * Tokenizes text by delimiter and returns matching options or new values (for freeSolo mode).
 * Filters out duplicates, disabled options, and non-matching values (unless freeSolo).
 */
export const tokenizeText = <T>({
  currentValues,
  delimiter,
  freeSolo,
  getOptionDisabled,
  getOptionLabel,
  options,
  text,
}: TokenizeTextParams<T>): T[] => {
  const parts = splitByDelimiter(text, delimiter);

  // If only one part (no delimiter found), return empty to let default behavior handle it
  if (parts.length <= 1) {
    return [];
  }

  const currentLabels = new Set(currentValues.map((v) => getOptionLabel(v)));
  const newValues: T[] = [];

  for (const part of parts) {
    // Skip if already selected
    if (currentLabels.has(part)) {
      continue;
    }

    // Try to find a matching option
    const matchingOption = options.find((opt) => getOptionLabel(opt) === part);

    if (matchingOption) {
      // Check if option is disabled
      if (getOptionDisabled?.(matchingOption)) {
        continue;
      }
      newValues.push(matchingOption);
      currentLabels.add(part); // Track to avoid duplicates
    } else if (freeSolo) {
      // In freeSolo mode, add as string value
      newValues.push(part as unknown as T);
      currentLabels.add(part);
    }
    // If not freeSolo and no matching option, skip this value
  }

  return newValues;
};
